import { forwardRef, useState, useEffect, useContext, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { GeoJSON } from 'leaflet';

import { noop } from 'utils';

import { MapContext } from './map-container';
import { MAP_GEO_JSON_DEFAULT_OPTIONS } from './options';

export const MapGeoJson = forwardRef(
	({ data, children, options, onInitialized = noop }, ref) => {
		const [geoJson, setGeoJson] = useState(null);
		const map = useContext(MapContext);

		useImperativeHandle(ref, () => geoJson, [geoJson]);

		useEffect(() => {
			if (map === null) {
				return;
			}

			const geoJson = new GeoJSON(data, {
				...MAP_GEO_JSON_DEFAULT_OPTIONS,
				...options,
			});
			setGeoJson(geoJson);

			map.addLayer(geoJson);
			map.fitBounds(geoJson.getBounds());

			map.invalidateSize();

			const teardown = onInitialized(map, geoJson);

			return () => {
				if (typeof teardown === 'function') {
					teardown();
				}

				geoJson.remove();
				setGeoJson(null);
			};
		}, [map, data, options, onInitialized]);

		return children;
	}
);

MapGeoJson.propTypes = {
	data: PropTypes.object.isRequired,
	options: PropTypes.shape({
		style: PropTypes.shape({
			fillColor: PropTypes.string,
			fillOpacity: PropTypes.number,
			color: PropTypes.string,
			weight: PropTypes.number,
			opacity: PropTypes.number,
		}),
		interactive: PropTypes.bool,
	}),
	onInitialized: PropTypes.func,
};
